import { NgStyle } from '@angular/common';
import { AfterContentInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, Renderer2 } from '@angular/core';
import { RouterLink } from '@angular/router';
import { ProductComponentCrosssellsDef } from '@df/catalog-ui/product/product.component.crosssells.def';
import { IProductDataV2 } from '@df/catalog/product/product';
import { App } from '@df/core/app';
import { DfModuleGtmModule } from '@df/module-gtm/df-module-gtm.module';
import { TimeoutService } from '@df/ng/timeout.service';
import { WishlistItem } from '@df/session/api/wishlist/wishlist-item';
import { ShellComponent } from '@df/ui/common/shell/shell.component';
import { TruncatePipe } from '@df/ui/common/truncate.pipe';
import { TranslatePipe } from '@df/ui/translation/translate.pipe';
import { ButtonStyleComponent } from '../../../common/buttons/button-style/button-style.component';
import { SwatchComponent } from '../../attribute/swatch/swatch.component';
import { ProductAltImageComponent } from '../alt-image/product-alt-image.component';
import { ProductPriceComponent } from '../price/product-price.component';
import { Product } from '../product.decorator';
import { ProductWishlistToggleComponent } from '../wishlist-toggle/product-wishlist-toggle.component';
import { ProductPlpTrustpilotReviewsComponent } from '../plp-trustpilot-reviews/product-plp-trustpilot-reviews';

@Component({
    imports: [
        ShellComponent,
        RouterLink,
        DfModuleGtmModule,
        ProductAltImageComponent,
        NgStyle,
        ButtonStyleComponent,
        ProductWishlistToggleComponent,
        SwatchComponent,
        ProductPriceComponent,
        TranslatePipe,
        TruncatePipe,
        ProductPlpTrustpilotReviewsComponent
    ],
    selector: 'product',
    templateUrl: 'product.html',
    styleUrl: 'product.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProductComponent extends ProductComponentCrosssellsDef<Product<IProductDataV2>> implements AfterContentInit {
    /**
     * Min number of swatches
     */
    override minSwatches = 2;

    /**
     * Optional wishlist item in wishlist context
     */
    @Input()
    wishlistItem?: WishlistItem;

    /**
     * Image height for 2+ cols layout
     */
    private imageHeightStyle?: string;

    constructor(
        changeDetectorRef: ChangeDetectorRef,
        app: App,
        timeoutService: TimeoutService,
        elementRef: ElementRef,
        private renderer2: Renderer2
    ) {
        super(changeDetectorRef, app, timeoutService, elementRef);
    }

    /**
     * On init actions
     */
    ngAfterContentInit() {
        this.timeoutService.setTimeout(() => {
            this.renderer2.setStyle(this.elementRef.nativeElement as HTMLElement, 'visibility', 'visible');
        });
    }

    /**
     * Retrieves image padding = x = height/width * 100
     *
     * We used this rather then ratio to make thing match more accurately without creating new ratios
     */
    getImageHeightStyle(): string {
        if (!this.imageHeightStyle || !this.requiresSizeWatch()) {
            return `${this.get1x1Ratio() * 100}%`;
        }
        return this.imageHeightStyle;
    }

    /**
     * Gets product image for given layout
     */
    getImage(): string | undefined {
        if (this.getLayout() === '2x1') {
            return this.model?.data?.img_horizontal || this.model?.data?.image;
        }
        return this.model?.data?.image;
    }

    /**
     * Gets product image for given layout
     */
    getAlternativeImage(): string | undefined {
        if (this.getLayout() === '2x1') {
            return undefined;
        }
        return this.model?.data?.img_alternative;
    }

    /**
     * If size watch is required
     */
    protected override requiresSizeWatch() {
        return this.getLayout() !== '1x1';
    }

    /**
     * On component size change callback
     */
    protected override onSizeChange(dims: { height: number; width: number }) {
        this.imageHeightStyle = this.listing?.plugin.grid.getImageHeight(this.get1x1Ratio(), this.gridColumn ?? 1, dims.width);
        this.detectChanges();
    }

    /**
     * Returns image ration for 1x1 column dependin on product type
     */
    private get1x1Ratio(): number {
        return this.isBannerWithTextOverImage() ? 1.25 : 1;
    }

    /**
     * on mobile we always use 1x1 layout as we have up to 2 cols in grid
     * and 2-col item take a whole row then and keeps the original ratio
     */
    override getLayout() {
        if (this.client.isS) {
            return '1x1';
        }
        return super.getLayout();
    }

    /**
     * If display is a banner with text over image
     */
    isBannerWithTextOverImage(): boolean {
        return (this.model.data?.isBanner && this.model.data.banner_overlay) || false;
    }

    get shortDescription(): string {
        return this.model.data?.short_description || this.currentParent?.data?.short_description || '';
    }

    get truncateLength(): number {
        return this.client.sizeId > 1 ? 85 : 45;
    }
}
